import React, { useEffect, useRef, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import StateManagedSelect from 'react-select';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import PlusIcon from '../../../components/icons/PlusIcon';
import selectStyles from '../../../util/selectStyles';

const Label = styled.div`
  font-family: 'Poppins-SemiBold';
  font-weight: 600;
  font-size: 14px;
  color: #23292E;
`;

const Description = styled.div`
  font-weight: 400;
  font-size: 12px;
  color: #23292E;
`;

const IconLarge = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  font-size: 20px;
  color: #45535F;
  padding: 4px 8px;
  cursor: pointer;
`;

const incrementTime = {
  hours: 2,
  minutes: 15,
};

const maxAttempts = 5;

const getWaitTimeOptions = (unit) => {
  const limits = { hours: 12, minutes: 4 };
  const options = [];
  for (let i = 1; i <= limits[unit]; i++) {
    const value = i * incrementTime[unit];
    options.push({ value, label: value });
  }
  return options;
};

const WaitingTimeRow = ({ waitingTime, index, onChange }) => {
  const { t } = useTranslation();

  const unitOptions = [
    { value: 'minutes', label: t('outbound_follow_ups.minutes') },
    { value: 'hours', label: t('outbound_follow_ups.hours') },
  ];

  const [timeOptions, setTimeOptions] = useState(
    getWaitTimeOptions(waitingTime.waiting_time_type || 'hours')
  );

  const handleUnitChange = (option) => {
    const newOptions = getWaitTimeOptions(option.value);
    setTimeOptions(newOptions);
    onChange({ ...waitingTime, waiting_time_type: option.value, waiting_time: newOptions[0].value });
  };

  const handleTimeChange = (option) => {
    onChange({ ...waitingTime, waiting_time: option.value });
  };

  return (
    <tr>
      <td className="p-2" style={{ width: '28%' }}>
        {t('outbound_follow_ups.attempts')} {index}
      </td>
      <td className="p-2" style={{ width: '33%' }}>
        <div className="figma-field mb-0 input-mercately-border">
          <label>{t('outbound_follow_ups.waitingSelectLabel')}</label>
          <StateManagedSelect
            options={timeOptions}
            placeholder={t('outbound_follow_ups.selectPlaceholder')}
            className="select-tag"
            classNamePrefix="select-tag"
            styles={selectStyles}
            onChange={handleTimeChange}
            value={timeOptions.find((o) => o.value === waitingTime.waiting_time) || timeOptions[0]}
            components={{ IndicatorSeparator: () => null }}
          />
        </div>
      </td>
      <td className="p-2" style={{ width: '29%' }}>
        <div className="figma-field mb-0 input-mercately-border">
          <label>{t('outbound_follow_ups.waitingUnitSelectLabel')}</label>
          <StateManagedSelect
            options={unitOptions}
            placeholder={t('outbound_follow_ups.selectPlaceholder')}
            className="select-tag"
            classNamePrefix="select-tag"
            styles={selectStyles}
            onChange={handleUnitChange}
            value={unitOptions.find((o) => o.value === waitingTime.waiting_time_type)}
            components={{ IndicatorSeparator: () => null }}
          />
        </div>
      </td>
      <td className="p-2" style={{ width: '10%' }}>
        <IconLarge>
          <div
            className="tooltip-top"
            onClick={() => onChange({ ...waitingTime, _destroy: true })}
          >
            <i className="mi-trash-outline">
              <div className="tooltiptext--black bg-dark tooltiptext tooltip-stats-view font-weight-normal">
                {t('outbound_follow_ups.delete')}
              </div>
            </i>
          </div>
        </IconLarge>
      </td>
    </tr>
  );
};

const OutboundFollowUpWaitingTimes = ({ waitingTimes, onChange, errors }) => {
  const { t } = useTranslation();
  const onChangeRef = useRef(onChange);
  useEffect(() => { onChangeRef.current = onChange; });

  const [items, setItems] = useState(
    (waitingTimes || []).map((wt) => ({
      ...wt,
      _uuid: wt._uuid || wt.id || uuidv4(),
      waiting_time: wt.waiting_time || getWaitTimeOptions(wt.waiting_time_type || 'hours')[0].value,
      waiting_time_type: wt.waiting_time_type || 'hours',
    }))
  );

  const activeItems = items.filter((wt) => !wt._destroy);
  const mountedRef = useRef(false);

  const handleAdd = () => {
    if (activeItems.length >= maxAttempts) return;
    const newItem = {
      _uuid: uuidv4(),
      waiting_time: getWaitTimeOptions('hours')[0].value,
      waiting_time_type: 'hours',
      _new: true,
    };
    setItems([...items, newItem]);
  };

  const handleChange = (uuid, updated) => {
    setItems(items.map((wt) => (wt._uuid === uuid ? { ...updated, _uuid: uuid } : wt)));
  };

  useEffect(() => {
    if (!mountedRef.current) { mountedRef.current = true; return; }
    onChangeRef.current(items);
  }, [items]);

  return (
    <div className="col-md-12 flex-column" style={{ marginTop: '24px' }}>
      <Label className="mb-8">{t('outbound_follow_ups.waitingLabel')}</Label>
      <Description>{t('outbound_follow_ups.waitingDescription')}</Description>
      <table className="mt-16 mb-8 w-100">
        <tbody>
          {activeItems.map((wt, index) => (
            <WaitingTimeRow
              key={wt._uuid}
              index={index + 1}
              waitingTime={wt}
              onChange={(updated) => handleChange(wt._uuid, updated)}
            />
          ))}
        </tbody>
      </table>
      {errors && (
        <span className="ml-10 error-msg fz-12">{errors}</span>
      )}
      {activeItems.length < maxAttempts && (
        <div className="mt-8">
          <div
            className="cursor-pointer figma-icon d-flex align-items-center justify-content-center p-4"
            onClick={handleAdd}
            style={{ color: '#198FCC', border: '1px solid #198FCC', borderRadius: '8px', width: '155px' }}
          >
            <span className="figma-label">{t('outbound_follow_ups.waitingAdd')}</span>
            <PlusIcon className="chatbot-plus-icon mx-5" />
          </div>
        </div>
      )}
    </div>
  );
};

export default OutboundFollowUpWaitingTimes;
